<template>
  <div class="quickstart-wrapper">
    <!-- Menu -->
    <aside class="menu-wrapper">
      <h3 class="menu-title">Getting started</h3>
      <div class="menu">
        <div
          v-for="(guide, index) in guides"
          :key="guide.title"
          class="menu-item"
          :class="{ selected: index === selectedGuideIndex }"
          @click="selectGuide(index)"
        >
          <unicon
            name="check"
            class="status"
            :class="{ completed: guide.isCompleted }"
          ></unicon>
          <span>{{ guide.title }}</span>
        </div>
      </div>
    </aside>

    <!-- Content -->
    <section
      v-for="(guide, index) in guides"
      :key="index"
      class="content"
      :class="{ selected: index === selectedGuideIndex }"
    >
      <h3>{{ guide.title }}</h3>

      <!-- Video thumbnail -->
      <div v-if="guide.video?.youtubeId" class="video-wrapper">
        <div class="video-block" @click="playVideo(guide.video.youtubeId)">
          <img :src="guide.video.thumbnailUrl" :alt="guide.title" />
          <span class="play-icon">▶</span>
        </div>
        <span class="video-description">{{ guide.video.description }}</span>
      </div>

      <!-- Description -->
      <div v-html="guide.description" class="description"></div>

      <!-- Button -->
      <button
        v-if="guide.link?.url"
        class="btn btn-small btn-primary"
        @click="guideButtonClicked(guide.link, index)"
      >
        {{ guide.link.title }}
      </button>
    </section>

    <!-- Video Modal -->
    <div v-if="showVideo" class="video-player-wrapper">
      <div class="player-wrapper">
        <div class="player-box">
          <div id="youtube-player"></div>
        </div>
        <unicon name="times" class="close-btn" @click="closeVideo"></unicon>
      </div>
    </div>
    <div
      class="bg-overlay"
      :class="{ show: showVideo }"
      @click="closeVideo"
    ></div>
  </div>
</template>

<script>
export default {
  name: "QuickstartGuide",
  props: {
    guides: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      selectedGuideIndex: 0,
      showVideo: false,
      player: null,
      currentVideoId: null,
    };
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    selectedApp() {
      return this.$store.getters.selectedApp;
    },
  },
  created() {
    this.setInitialGuide();
  },
  methods: {
    setInitialGuide() {
      const firstIncomplete = this.guides.findIndex((g) => !g.isCompleted);
      if (firstIncomplete !== -1) {
        this.selectedGuideIndex = firstIncomplete;
      } else {
        this.selectedGuideIndex = this.guides.length - 1;
      }
    },

    selectGuide(index) {
      this.$emit("guideSelected", index);
      this.selectedGuideIndex = index;
    },

    async loadYouTubeIframeAPI() {
      return new Promise((resolve) => {
        if (window.YT && window.YT.Player) {
          return resolve(window.YT);
        }
        window.onYouTubeIframeAPIReady = () => resolve(window.YT);
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
      });
    },

    async playVideo(videoId) {
      this.showVideo = true;
      this.currentVideoId = videoId;

      const YT = await this.loadYouTubeIframeAPI();

      this.$nextTick(() => {
        if (!this.player) {
          this.player = new YT.Player("youtube-player", {
            height: "363",
            width: "640",
            videoId,
            events: {
              onReady: (event) => event.target.playVideo(),
            },
          });
        } else {
          this.player.loadVideoById(videoId);
          this.player.playVideo();
        }
      });
    },

    guideButtonClicked(link, index) {
      this.$emit("guideButtonClicked", index);

      if (link.linkType === "internal") {
        this.$router.push({ path: link.url });
      } else if (link.linkType === "external") {
        window.open(link.url, "_blank").focus();
      } else if (link.linkType === "zendesk") {
        window
          .open(
            `https://${this.selectedApp.credentials?.subdomain}.zendesk.com/${link.url}`,
            "_blank"
          )
          .focus();
      }
    },

    closeVideo() {
      this.showVideo = false;
      if (this.player) {
        this.player.pauseVideo();
        this.player.destroy();
        this.player = null;
      }
    },
  },
  beforeDestroy() {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
  },
};
</script>

<style scoped lang="scss">
.quickstart-wrapper {
  position: relative;
  display: flex;
  flex-direction: row;
  margin: 3rem 0 5rem;
  z-index: 1;
}

.menu-wrapper {
  position: relative;
  width: 20rem;
  margin-right: -1px;
  z-index: 1;
}

.menu-title {
  font-size: 1rem;
  font-weight: $mediumFontWeight;
  font-size: $smallerFontSize;
  color: $greyColor;
  margin-bottom: 1rem;
}

.menu-item {
  display: flex;
  align-items: flex-start;
  padding: 0.5rem;
  margin: 0.25rem 0;
  cursor: pointer;
  border-radius: 0.5rem;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border: 1px solid transparent;

  span {
    font-size: $smallFontSize;
    color: $greyColor;
    opacity: 0.75;
    margin-right: 1rem;
    line-height: 1.4;
  }

  &:hover {
    span {
      opacity: 1;
    }
  }

  .unicon ::v-deep {
    svg {
      fill: $lightGreyColor;
      width: 1.15rem;
      height: 1.15rem;
      padding: 0.05rem;
      margin-right: 0.85rem;
      background: lighten($lightGreyColor, 25%);
      border-radius: 50%;
    }

    &.completed {
      svg {
        background: $greenStatusColor;
        fill: darken($greenStatusColor, 40%);
      }
    }
  }

  &.selected {
    background: darken($whiteColor, 2.5%);
    border: 1px solid $inputBorderColor;
    border-right: none;

    span {
      opacity: 1;
      font-weight: $mediumFontWeight;
    }
  }
}

.content {
  display: none;
  flex: 1;
  padding: 1.5rem 2rem;
  background: darken($whiteColor, 2.5%);
  border: 1px solid $inputBorderColor;
  border-radius: 1rem;

  &.selected {
    display: block;
  }

  h3 {
    font-size: $mediumFontSize;
    margin-bottom: 1rem;
  }

  .description {
    ::v-deep {
      font-size: $smallFontSize;
      line-height: 1.4;
      color: $greyColor;

      * {
        font-size: $smallFontSize;
        line-height: 1.4;
        color: $greyColor;
      }

      ol li {
        list-style: decimal;
      }

      ul li {
        list-style: disc;
      }

      ul,
      ol {
        margin-left: 2rem;
        margin-top: 0.5rem;
      }

      li {
        margin-bottom: 0.25rem;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}

.btn {
  margin-top: 1.5rem;
}

.video-wrapper {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 1.5rem;
  margin-bottom: 2rem;

  .video-block {
    position: relative;
    width: 320px;
    height: 180px;
    flex: 1;
    cursor: pointer;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 0.5rem;
    }

    .play-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.6);
      color: white;
      padding: 1.25rem;
      line-height: 1.5rem;
      border-radius: 50%;
      font-size: 1.5rem;
      width: 1.5rem;
      height: 1.5rem;
      text-align: center;
      backdrop-filter: blur(5px);
    }
  }

  .video-description {
    flex: 1;
    font-size: $smallerFontSize;
    font-style: italic;
    color: $greyColor;
  }
}

.video-player-wrapper {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  .player-wrapper {
    position: relative;
  }

  .player-box {
    position: relative;
    background: $blackColor;
    border-radius: 1rem;
    overflow: hidden;

    #youtube-player {
      width: 640px;
      height: 390px;
    }
  }

  .close-btn {
    position: absolute;
    top: -1rem;
    right: -1rem;
    border: none;
    z-index: 2;

    ::v-deep {
      svg {
        fill: $greyColor;
        background: $whiteColor;
        border-radius: 50%;
        width: 2rem;
        height: 2rem;
        cursor: pointer;
      }
    }
  }
}
</style>
